import { BlockStack, LegacyTabs } from '@shopify/polaris';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AppSaveBar from '@/components/AppSaveBar/AppSaveBar';
import Layout from '@/components/layout';
import SkeletonLayoutWithCard from '@/components/SkeletonLayoutWithCard';
import { config } from '@/config';
import { PricingPlan, WIDGET_DISPLAY_CHOICE, WIDGET_SETTING_CHOICE } from '@/constants/enum';
import { arrayToStringByComma, hexToRgbaObject, isAvailableFeature, openErrorToast, openToast, rgbaToHex } from '@/helpers';
import { checkDirtyField } from '@/helpers/compare';
import {
  useGetAppearanceSettingQuery,
  useGetDetailSettingShippingQuery,
  useGetGeneralSettingQuery,
  useGetShopThemesQuery,
  useGetWidgetAppearanceTextDetailQuery,
  useGetWidgetAppearanceTextQuery,
  useUpdateAppearanceTextMutation,
  useUpdateGeneralSettingMutation,
  useUpdateWidgetSettingMutation,
} from '@/redux/query';
import {
  currentWidgetDataSelector,
  handleResetWidget,
  handleResetWidgetColorError,
  handleResetWidgetState,
  handleSaveBackupWidgetColors,
  handleSaveBackupWidgetSettings,
  handleSaveBackupWidgetTextDefaultDetail,
  handleSaveWidgetColors,
  handleSaveWidgetSettings,
  handleSaveWidgetTextDefaultDetail,
  handleSetWidgetAppearanceDisable,
  handleSetWidgetColorError,
  handleSetWidgetState,
  oldWidgetDataSelector,
  widgetColorErrorsSelector,
  widgetColorSelector,
  widgetSettingSelector,
  widgetTextDefaultDetailSelector,
} from '@/redux/slice/widget.slice';
import { ColorError, IMainWidgetSettings, IWidgetColorValidation } from '@/types/components/widget';
import { useDispatch, useSelector } from 'react-redux';
import CTABanner from '../../components/BannerTemplate/CustomizeDesignBanner';
import WidgetPosition from './component/WidgetPosition';

import FooterHelpText from '@/components/FooterHelpText/FooterHelpText';
import WidgetPreview from '@/modules/WidgetPreview';
import { handleSaveShippingSettings } from '@/redux/slice/shipping.slice';
import {
  handleResetWidgetTextDetail,
  handleSaveWidgetTextDetail,
  handleSaveWidgetTextDetailBackup,
} from '@/redux/slice/widgetText.slice';
import WidgetDesign from './component/WidgetDesign';
import { PolarisLayout, StyledSection } from './styled';
import { handleSetSaveBar } from '@/redux/slice/savebar.slice';
import { handleSaveBackupSettings, handleSaveSettings } from '@/redux/slice/setting.slice';
import { handleSetErrorFields } from '@/redux/slice/error.slice';
import { handleLocationTab, widgetTabSelector } from '@/redux/slice/tab.slice';

export default function WidgetAppearancePage() {
  const { t } = useTranslation('widget_settings');
  const widgetSettings = useSelector(widgetSettingSelector);
  const widgetColors = useSelector(widgetColorSelector);
  const widgetColorErrors = useSelector(widgetColorErrorsSelector);
  const oldWidgetData = useSelector(oldWidgetDataSelector);
  const currentWidgetData = useSelector(currentWidgetDataSelector);
  const dispatch = useDispatch();
  const {
    data: widgetSettingData,
    isLoading: isLoadingWidgetSetting,
    refetch: refetchingWidgetSettingData,
  } = useGetAppearanceSettingQuery({ shop: config.shop });
  const { data: detailShippingSettingData, isLoading: isLoadingShippingSettingData } = useGetDetailSettingShippingQuery();
  const [updateWidgetSetting, { isLoading: isLoadingUpdateWidgetSetting }] = useUpdateWidgetSettingMutation();
  const {
    data: generalSettingData,
    refetch: refetchGeneralSettingData,
    isLoading: isLoadingGeneralSettingData,
  } = useGetGeneralSettingQuery({ shop: config.shop });
  const [updateGeneralSetting] = useUpdateGeneralSettingMutation();

  const { data: shopThemesData, isLoading: isLoadingShopThemes } = useGetShopThemesQuery({ shop: config.shop });
  const {
    data: widgetAppearanceTextDetail,
    refetch: refetchWidgetAppearanceTextDetail,
    isLoading: isLoadingWidgetTextDetail,
  } = useGetWidgetAppearanceTextDetailQuery({
    shop: config.shop,
    defaultLanguage: true,
  });

  const widgetTextDefaultDetail = useSelector(widgetTextDefaultDetailSelector);
  const [updateAppearanceText, { isLoading: isLoadingUpdateAppearanceText }] = useUpdateAppearanceTextMutation();
  const { refetch: refetchFullWidgetAppearanceText } = useGetWidgetAppearanceTextQuery({
    shop: config.shop,
  });

  const widgetChoice = useSelector(widgetTabSelector);
  const [isSaving, setIsSaving] = useState(false);

  const isOnlyShowCartPage = useMemo(
    () =>
      widgetSettings.widgetPosition.displayOnPage.length === 1 &&
      widgetSettings.widgetPosition.displayOnPage.includes(WIDGET_DISPLAY_CHOICE.CART),
    [widgetSettings.widgetPosition.displayOnPage],
  );

  const isOnlyShowProductPage = useMemo(
    () =>
      widgetSettings.widgetPosition.displayOnPage.length === 1 &&
      widgetSettings.widgetPosition.displayOnPage.includes(WIDGET_DISPLAY_CHOICE.PRODUCT),
    [widgetSettings.widgetPosition.displayOnPage],
  );

  const isShowBothPage = useMemo(
    () => widgetSettings.widgetPosition.displayOnPage.length === 2,
    [widgetSettings.widgetPosition.displayOnPage.length],
  );

  const handleOnSubmit = async () => {
    let isValid = true;
    dispatch(handleSetWidgetState({ isChecking: true }));

    const newWidgetColorErrors = structuredClone(widgetColorErrors);

    Object.keys(widgetColorErrors).forEach((key) => {
      const keyErrors = widgetColorErrors[key as keyof IWidgetColorValidation] as Record<string, ColorError>;
      Object.keys(keyErrors).forEach((subKey) => {
        const subKeyError = keyErrors[subKey];
        if (subKeyError?.error) {
          (newWidgetColorErrors[key as keyof IWidgetColorValidation] as Record<string, ColorError>)[subKey] = {
            ...subKeyError,
            isShow: true,
          };
          isValid = false;
        }
      });
    });

    dispatch(handleSetWidgetColorError(newWidgetColorErrors));

    if (!isValid) {
      console.log('Invalid form');
      return;
    }

    setIsSaving(true);
    updateWidgetSetting({
      shop: widgetSettings.shop,
      sortMethods: widgetSettings.sortMethods,
      defaultMethod: widgetSettings.defaultMethod,
      timezone: widgetSettings.timeZone,
      widgetPosition: {
        displayOnPage: widgetSettings.widgetPosition.displayOnPage,
        cartPageCustomSelector: widgetSettings.widgetPosition.cartPageSelector,
        cartPageCustomPosition: widgetSettings.widgetPosition.cartPagePosition,
        cartDrawerCustomSelector: widgetSettings.widgetPosition.cartDrawerSelector,
        cartDrawerCustomPosition: widgetSettings.widgetPosition.cartDrawerPosition,
        productPageCustomSelector: widgetSettings.widgetPosition.productPageSelector,
        productPageCustomPosition: widgetSettings.widgetPosition.productPagePosition,
      },
      widgetDisplayMethods: {
        layout: widgetSettings.widgetMethods.layout,
        headerDisplayType: widgetSettings.widgetMethods.displayType,
        headerBorderRadius: widgetSettings.widgetMethods.borderRadius,
        locationLayout: widgetSettings.widgetMethods.locationLayout,
        hideHeader: widgetSettings.widgetMethods.hideHeader,
        hideHeadingTitle: widgetSettings.widgetMethods.hideHeadingTitle,
        hideCheckIcon: widgetSettings.widgetMethods.hideCheckIcon,
        datePickerAlignment: widgetSettings.widgetMethods.datePickerAlignment,
        hidePickupLocation: widgetSettings.widgetMethods.hidePickupLocation,
      },
      customizations: {
        showOutsideDays: widgetSettings.customizations.showOutsideDays,
        alwaysShowCalendar: widgetSettings.customizations.alwaysShowCalendar,
        captionLayout: widgetSettings.customizations.captionLayout,
        weekStartsOn: Number(widgetSettings.customizations.weekStartsOn),
        locale: widgetSettings.customizations.locale,
        dateFormat: widgetSettings.customizations.dateFormat,
        timeFormat: widgetSettings.customizations.timeFormat,
        showWeekNumber: widgetSettings.customizations.showWeekNumber,
        showWeekDays: widgetSettings.customizations.showWeekDays,
        fixedWeeks: widgetSettings.customizations.fixedWeeks,
        rightToLeft: widgetSettings.customizations.rightToLeft,
        hideNavigation: widgetSettings.customizations.hideNavigation,
        startOnThisMonth: widgetSettings.customizations.startOnThisMonth,
        disableNavigation: widgetSettings.customizations.disableNavigation,
        navigationLayout: widgetSettings.customizations.navigationLayout,
      },
      sizes: {
        borderRadius: widgetSettings.sizes.borderRadius,
        headingFontSize: widgetSettings.sizes.headingFontSize,
        widgetSize: widgetSettings.sizes.widgetSize,
        errorMessageFontSize: widgetSettings.sizes.errorMessageFontSize,
      },
      headerTitle: widgetSettings.headerTitle,
      additionalMessage: widgetSettings.additionalMessage,
      customMessage: widgetSettings.customMessage,
      additionalCss: widgetSettings.additionalCss,
      colorScheme: widgetSettings.colorScheme,
      calendarColor: {
        calendarBackground: rgbaToHex(widgetColors.calendar.calendarBackground),
        calendarBorder: rgbaToHex(widgetColors.calendar.calendarBorder),
        selectedDateColor: rgbaToHex(widgetColors.calendar.selectedDateColor),
        selectedDateBackgroundColor: rgbaToHex(widgetColors.calendar.selectedDateBackgroundColor),
        availableDateColor: rgbaToHex(widgetColors.calendar.availableDateColor),
        unavailableDateColor: rgbaToHex(widgetColors.calendar.unavailableDateColor),
      },
      headerColor: {
        headerTitleColor: rgbaToHex(widgetColors.header.headerTitleColor),
        buttonColor: rgbaToHex(widgetColors.header.buttonColor),
        buttonBackground: rgbaToHex(widgetColors.header.buttonBackground),
        buttonBorder: rgbaToHex(widgetColors.header.buttonBorder),
        hoverButtonBackground: rgbaToHex(widgetColors.header.hoverButtonBackground),
        hoverButtonColor: rgbaToHex(widgetColors.header.hoverButtonColor),
        hoverButtonBorder: rgbaToHex(widgetColors.header.hoverButtonBorder),
        selectedButtonBackground: rgbaToHex(widgetColors.header.selectedButtonBackground),
        selectedButtonColor: rgbaToHex(widgetColors.header.selectedButtonColor),
        selectedButtonBorder: rgbaToHex(widgetColors.header.selectedButtonBorder),
        dateTitleColor: rgbaToHex(widgetColors.header.dateTitleColor),
        timeTitleColor: rgbaToHex(widgetColors.header.timeTitleColor),
        inputBackground: rgbaToHex(widgetColors.header.inputBackground),
        inputBorder: rgbaToHex(widgetColors.header.inputBorder),
        inputTextDateColor: rgbaToHex(widgetColors.header.inputTextDateColor),
        inputTextTimeColor: rgbaToHex(widgetColors.header.inputTextTimeColor),
      },
      bodyColor: {
        inputColor: rgbaToHex(widgetColors.body.inputColor),
        inputBackground: rgbaToHex(widgetColors.body.inputBackground),
        inputBorder: rgbaToHex(widgetColors.body.inputBorder),
        checkButtonColor: rgbaToHex(widgetColors.body.checkButtonColor),
        checkButtonBackground: rgbaToHex(widgetColors.body.checkButtonBackground),
        checkButtonBorder: rgbaToHex(widgetColors.body.checkButtonBorder),
        eligibleMessageColor: rgbaToHex(widgetColors.body.eligibleMessageColor),
        requiredMessageColor: rgbaToHex(widgetColors.body.requiredMessageColor),
        errorColor: rgbaToHex(widgetColors.body.errorColor),
        additionalNotesColor: rgbaToHex(widgetColors.body.additionalNotesColor),
      },
    })
      .unwrap()
      .then(() => {
        updateAppearanceText({
          shop: config.shop,
          code: widgetTextDefaultDetail.code || 'en-US',
          calendarLocale: widgetTextDefaultDetail.calendarLocale,
          defaultLanguage: true,
          name: widgetTextDefaultDetail.name || '',
          headerTitle: widgetTextDefaultDetail.headerTitle || '',
          additionalMessage: widgetTextDefaultDetail.additionalMessage || '',
          texts: widgetTextDefaultDetail.texts,
        })
          .unwrap()
          .then(() => {
            return updateGeneralSetting({
              shop: config.shop,
              isDisableCheckoutButton: isOnlyShowProductPage ? false : generalSettingData?.data.isDisableCheckoutButton,
              isDisableAddToCartButton:
                isOnlyShowCartPage || isShowBothPage ? false : generalSettingData?.data.isDisableAddToCartButton,
            }).unwrap();
          })
          .then(() => refetchGeneralSettingData())
          .then(() => refetchWidgetAppearanceTextDetail())
          .then(() => refetchingWidgetSettingData())
          .then(() => refetchFullWidgetAppearanceText())
          .then(() => {
            openToast(dispatch);
            dispatch(handleResetWidgetState());
            dispatch(handleResetWidgetColorError());
            setIsSaving(false);
          })
          .catch(() => {
            openErrorToast(dispatch);
            dispatch(handleResetWidgetState());
            dispatch(handleResetWidgetColorError());
            setIsSaving(false);
          });
      })
      .catch(() => {
        openErrorToast(dispatch);
        dispatch(handleResetWidgetColorError());
        dispatch(handleResetWidgetState());
      });
  };

  useEffect(() => {
    dispatch(
      handleSetWidgetAppearanceDisable({
        isDisableCustomStyle: !isAvailableFeature(PricingPlan.Essential, generalSettingData?.data.plan as PricingPlan),
        isDisableAutoTranslate: !isAvailableFeature(PricingPlan.Essential, generalSettingData?.data.plan as PricingPlan),
      }),
    );
  }, [generalSettingData?.data.plan, dispatch]);

  const handleOnDiscard = () => {
    dispatch(handleSetSaveBar({ isDiscard: true }));
    dispatch(handleResetWidget());
    dispatch(handleResetWidgetTextDetail());
  };

  const isChangeData = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { widgetState: oldWidgetState, ...oldRest } = oldWidgetData;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { widgetState: currentWidgetState, ...currentRest } = currentWidgetData;
    return checkDirtyField(currentRest, oldRest).isDirty;
  }, [oldWidgetData, currentWidgetData]);

  useEffect(() => {
    if (generalSettingData?.data) {
      dispatch(
        handleSaveSettings({
          shop: config.shop,
          googleMapApiKey: generalSettingData?.data.googleMapApiKey,
          isRequiredBeforeCheckout: generalSettingData?.data.isRequiredBeforeCheckout,
          isShowOnProductPage: generalSettingData?.data.isShowOnProductPage,
          showMapOnWidget: generalSettingData?.data.showMapOnWidget,
          dateTimeOnPackingSlip: generalSettingData?.data.dateTimeOnPackingSlip,
          includeNonWorkDayInPrep: generalSettingData?.data.includeNonWorkDayInPrep,
          kindOfOverrideRules: generalSettingData?.data.kindOfOverrideRules,
          timezone: generalSettingData?.data.timezone,
          isEnableOrderTag: generalSettingData?.data.isEnableOrderTag,
          isAutoFillPickupAddress: generalSettingData?.data.isAutoFillPickupAddress,
          isModifiedPickupAddress: generalSettingData?.data.isModifiedPickupAddress,
          isChangeMethodShipping: generalSettingData?.data.isChangeMethodShipping,
          isDisableCheckoutButton: generalSettingData?.data.isDisableCheckoutButton,
          isDisableAddToCartButton: generalSettingData?.data.isDisableAddToCartButton,
          manualSelectorBlockCheckoutButton: arrayToStringByComma(
            generalSettingData?.data.manualSelectorBlockCheckoutButton || [],
          ),
          manualSelectorBlockAddToCartButton: arrayToStringByComma(
            generalSettingData?.data.manualSelectorBlockAddToCartButton || [],
          ),
          deliveryRestrictionType: generalSettingData?.data.deliveryRestrictionType,
          deliveryDistanceUnit: generalSettingData?.data.restrictionDistanceUnit,
          googleRoutesApiKey: generalSettingData?.data.googleRoutesApiKey,
          isEnableGoogleMap: generalSettingData?.data.isEnableGoogleMap,
        }),
      );
      dispatch(
        handleSaveBackupSettings({
          shop: config.shop,
          googleMapApiKey: generalSettingData?.data.googleMapApiKey,
          isRequiredBeforeCheckout: generalSettingData?.data.isRequiredBeforeCheckout,
          isShowOnProductPage: generalSettingData?.data.isShowOnProductPage,
          showMapOnWidget: generalSettingData?.data.showMapOnWidget,
          dateTimeOnPackingSlip: generalSettingData?.data.dateTimeOnPackingSlip,
          includeNonWorkDayInPrep: generalSettingData?.data.includeNonWorkDayInPrep,
          kindOfOverrideRules: generalSettingData?.data.kindOfOverrideRules,
          timezone: generalSettingData?.data.timezone,
          isEnableOrderTag: generalSettingData?.data.isEnableOrderTag,
          isAutoFillPickupAddress: generalSettingData?.data.isAutoFillPickupAddress,
          isModifiedPickupAddress: generalSettingData?.data.isModifiedPickupAddress,
          isChangeMethodShipping: generalSettingData?.data.isChangeMethodShipping,
          isDisableCheckoutButton: generalSettingData?.data.isDisableCheckoutButton,
          isDisableAddToCartButton: generalSettingData?.data.isDisableAddToCartButton,
          manualSelectorBlockCheckoutButton: arrayToStringByComma(
            generalSettingData?.data.manualSelectorBlockCheckoutButton || [],
          ),
          manualSelectorBlockAddToCartButton: arrayToStringByComma(
            generalSettingData?.data.manualSelectorBlockAddToCartButton || [],
          ),
          deliveryRestrictionType: generalSettingData?.data.deliveryRestrictionType,
          deliveryDistanceUnit: generalSettingData?.data.restrictionDistanceUnit,
          googleRoutesApiKey: generalSettingData?.data.googleRoutesApiKey,
          isEnableGoogleMap: generalSettingData?.data.isEnableGoogleMap,
        }),
      );
    }
    if (generalSettingData?.data.googleMapApiKey) {
      dispatch(
        handleSetErrorFields({
          errorFields: {
            errorValidGGApi: false,
          },
          isValidAPIKey: true,
        }),
      );
    }

    if (generalSettingData?.data.googleRoutesApiKey) {
      dispatch(
        handleSetErrorFields({
          errorFields: {
            errorValidGGRoutesApi: false,
          },
          isValidRoutesAPIKey: true,
        }),
      );
    }

    if (widgetSettingData?.data) {
      dispatch(
        handleSaveWidgetSettings({
          widgetPosition: {
            displayOnPage: widgetSettingData.data.widgetPosition.displayOnPage as WIDGET_DISPLAY_CHOICE[],
            cartPageSelector: widgetSettingData.data.widgetPosition.cartPageCustomSelector || '',
            cartPagePosition: widgetSettingData.data.widgetPosition.cartPageCustomPosition,
            cartDrawerSelector: widgetSettingData.data.widgetPosition.cartDrawerCustomSelector || '',
            cartDrawerPosition: widgetSettingData.data.widgetPosition.cartDrawerCustomPosition,
            productPageSelector: widgetSettingData.data.widgetPosition.productPageCustomSelector || '',
            productPagePosition: widgetSettingData.data.widgetPosition.productPageCustomPosition,
          },
        }),
      );
    }
  }, [dispatch, generalSettingData?.data, widgetSettingData?.data]);

  useEffect(() => {
    if (!widgetSettingData || !widgetSettingData?.data || !widgetAppearanceTextDetail?.data) {
      console.log('Data is undefined');
      return;
    }
    const defaultText = widgetAppearanceTextDetail?.data;

    if (widgetAppearanceTextDetail?.data?.texts?.length === 0) {
      refetchWidgetAppearanceTextDetail();
    }

    const widgetSetting: IMainWidgetSettings = {
      id: widgetSettingData.data.id,
      shop: widgetSettingData.data.shop,
      timeZone: widgetSettingData.data.timezone,
      sortMethods: widgetSettingData.data.sortMethods,
      defaultMethod: widgetSettingData.data.defaultMethod,
      widgetPosition: {
        displayOnPage: widgetSettingData.data.widgetPosition.displayOnPage as WIDGET_DISPLAY_CHOICE[],
        cartPageSelector: widgetSettingData.data.widgetPosition.cartPageCustomSelector || '',
        cartPagePosition: widgetSettingData.data.widgetPosition.cartPageCustomPosition,
        cartDrawerSelector: widgetSettingData.data.widgetPosition.cartDrawerCustomSelector || '',
        cartDrawerPosition: widgetSettingData.data.widgetPosition.cartDrawerCustomPosition,
        productPageSelector: widgetSettingData.data.widgetPosition.productPageCustomSelector || '',
        productPagePosition: widgetSettingData.data.widgetPosition.productPageCustomPosition,
      },
      widgetMethods: {
        layout: widgetSettingData.data.widgetDisplayMethods.layout,
        displayType: widgetSettingData.data.widgetDisplayMethods.headerDisplayType,
        borderRadius: widgetSettingData.data.widgetDisplayMethods.headerBorderRadius,
        locationLayout: widgetSettingData.data.widgetDisplayMethods.locationLayout,
        hideHeader: widgetSettingData.data.widgetDisplayMethods.hideHeader,
        hideHeadingTitle: widgetSettingData.data.widgetDisplayMethods.hideHeadingTitle,
        hideCheckIcon: widgetSettingData.data.widgetDisplayMethods.hideCheckIcon,
        datePickerAlignment: widgetSettingData.data.widgetDisplayMethods.datePickerAlignment,
        hidePickupLocation: widgetSettingData.data.widgetDisplayMethods.hidePickupLocation,
      },
      customizations: {
        captionLayout: widgetSettingData.data.customizations.captionLayout,
        showOutsideDays: widgetSettingData.data.customizations.showOutsideDays,
        showWeekNumber: widgetSettingData.data.customizations.showWeekNumber,
        showWeekDays: widgetSettingData.data.customizations.showWeekDays,
        fixedWeeks: widgetSettingData.data.customizations.fixedWeeks,
        weekStartsOn: widgetSettingData.data.customizations.weekStartsOn.toString(),
        rightToLeft: widgetSettingData.data.customizations.rightToLeft,
        hideNavigation: widgetSettingData.data.customizations.hideNavigation,
        startOnThisMonth: widgetSettingData.data.customizations.startOnThisMonth,
        alwaysShowCalendar: widgetSettingData.data.customizations.alwaysShowCalendar,
        disableNavigation: widgetSettingData.data.customizations.disableNavigation,
        navigationLayout: widgetSettingData.data.customizations.navigationLayout,
        dateFormat: widgetSettingData.data.customizations.dateFormat,
        timeFormat: widgetSettingData.data.customizations.timeFormat,
        locale: widgetSettingData.data.customizations.locale,
      },
      sizes: {
        borderRadius: widgetSettingData.data.sizes.borderRadius,
        widgetSize: widgetSettingData.data.sizes.widgetSize,
        headingFontSize: widgetSettingData.data.sizes.headingFontSize,
        errorMessageFontSize: widgetSettingData.data.sizes.errorMessageFontSize,
      },
      colorScheme: widgetSettingData.data.colorScheme,
      headerTitle: widgetSettingData.data.headerTitle,
      additionalMessage: widgetSettingData.data.additionalMessage,
      additionalCss: widgetSettingData.data.additionalCss,
      customMessage: widgetSettingData.data.customMessage || '',
    };

    const widgetColorData = {
      id: widgetSettingData.data.id,
      calendar: {
        calendarBackground: hexToRgbaObject(widgetSettingData.data.calendarColor.calendarBackground),
        calendarBorder: hexToRgbaObject(widgetSettingData.data.calendarColor.calendarBorder),
        selectedDateColor: hexToRgbaObject(widgetSettingData.data.calendarColor.selectedDateColor),
        selectedDateBackgroundColor: hexToRgbaObject(widgetSettingData.data.calendarColor.selectedDateBackgroundColor),
        availableDateColor: hexToRgbaObject(widgetSettingData.data.calendarColor.availableDateColor),
        unavailableDateColor: hexToRgbaObject(widgetSettingData.data.calendarColor.unavailableDateColor),
      },
      header: {
        headerTitleColor: hexToRgbaObject(widgetSettingData.data.headerColor.headerTitleColor),
        buttonColor: hexToRgbaObject(widgetSettingData.data.headerColor.buttonColor),
        buttonBackground: hexToRgbaObject(widgetSettingData.data.headerColor.buttonBackground),
        buttonBorder: hexToRgbaObject(widgetSettingData.data.headerColor.buttonBorder),
        hoverButtonBackground: hexToRgbaObject(widgetSettingData.data.headerColor.hoverButtonBackground),
        hoverButtonColor: hexToRgbaObject(widgetSettingData.data.headerColor.hoverButtonColor),
        hoverButtonBorder: hexToRgbaObject(widgetSettingData.data.headerColor.hoverButtonBorder),
        selectedButtonBackground: hexToRgbaObject(widgetSettingData.data.headerColor.selectedButtonBackground),
        selectedButtonColor: hexToRgbaObject(widgetSettingData.data.headerColor.selectedButtonColor),
        selectedButtonBorder: hexToRgbaObject(widgetSettingData.data.headerColor.selectedButtonBorder),
        dateTitleColor: hexToRgbaObject(widgetSettingData.data.headerColor.dateTitleColor),
        timeTitleColor: hexToRgbaObject(widgetSettingData.data.headerColor.timeTitleColor),
        inputBackground: hexToRgbaObject(widgetSettingData.data.headerColor.inputBackground),
        inputBorder: hexToRgbaObject(widgetSettingData.data.headerColor.inputBorder),
        inputTextDateColor: hexToRgbaObject(widgetSettingData.data.headerColor.inputTextDateColor),
        inputTextTimeColor: hexToRgbaObject(widgetSettingData.data.headerColor.inputTextTimeColor),
      },
      body: {
        inputColor: hexToRgbaObject(widgetSettingData.data.bodyColor.inputColor),
        inputBackground: hexToRgbaObject(widgetSettingData.data.bodyColor.inputBackground),
        inputBorder: hexToRgbaObject(widgetSettingData.data.bodyColor.inputBorder),
        checkButtonColor: hexToRgbaObject(widgetSettingData.data.bodyColor.checkButtonColor),
        checkButtonBackground: hexToRgbaObject(widgetSettingData.data.bodyColor.checkButtonBackground),
        checkButtonBorder: hexToRgbaObject(widgetSettingData.data.bodyColor.checkButtonBorder),
        eligibleMessageColor: hexToRgbaObject(widgetSettingData.data.bodyColor.eligibleMessageColor),
        requiredMessageColor: hexToRgbaObject(widgetSettingData.data.bodyColor.requiredMessageColor),
        errorColor: hexToRgbaObject(widgetSettingData.data.bodyColor.errorColor),
        additionalNotesColor: hexToRgbaObject(widgetSettingData.data.bodyColor.additionalNotesColor),
      },
    };

    dispatch(handleSaveWidgetSettings(widgetSetting));
    dispatch(handleSaveWidgetColors(widgetColorData));
    dispatch(handleSaveBackupWidgetSettings(widgetSetting));
    dispatch(handleSaveBackupWidgetColors(widgetColorData));

    dispatch(handleSaveWidgetTextDetail(defaultText));
    dispatch(handleSaveWidgetTextDetailBackup(defaultText));
    dispatch(handleSaveWidgetTextDefaultDetail(defaultText));
    dispatch(handleSaveBackupWidgetTextDefaultDetail(defaultText));
  }, [dispatch, refetchWidgetAppearanceTextDetail, widgetAppearanceTextDetail?.data, widgetSettingData]);

  useEffect(() => {
    dispatch(
      handleSetWidgetState({
        isDisableAdditionalMessage: !isAvailableFeature(PricingPlan.Enterprise, generalSettingData?.data.plan as PricingPlan),
      }),
    );
  }, [dispatch, generalSettingData?.data.plan]);

  useEffect(() => {
    dispatch(
      handleSaveShippingSettings({
        isActive: detailShippingSettingData?.data.isActive,
        isActiveDatePicker: detailShippingSettingData?.data.isActiveDatePicker,
      }),
    );
  }, [detailShippingSettingData?.data.isActive, detailShippingSettingData?.data.isActiveDatePicker, dispatch]);

  const tabs = [
    {
      id: 'widget-position',
      content: t('position.title'),
      accessibilityLabel: 'Widget positions',
      panelID: 'widget-position',
    },
    {
      id: 'widget-design',
      content: 'Design',
      accessibilityLabel: 'Widget design',
      panelID: 'widget-design',
    },
  ];

  const handleWidgetChoice = useCallback(
    (selectedTabIndex: number) => {
      dispatch(handleLocationTab({ key: 'widgetTab', value: selectedTabIndex }));
    },
    [dispatch],
  );

  return (
    <Layout title={t('page.title')}>
      {isLoadingWidgetSetting ||
      isLoadingGeneralSettingData ||
      isLoadingShopThemes ||
      isLoadingShippingSettingData ||
      isLoadingWidgetTextDetail ? (
        <SkeletonLayoutWithCard />
      ) : (
        <BlockStack gap={'400'}>
          <CTABanner
            isOpen={Boolean(generalSettingData?.data.showBannerContact)}
            onClose={() =>
              updateGeneralSetting({
                shop: config.shop,
                showBannerContact: false,
              })
            }
          />
          <AppSaveBar
            open={isChangeData}
            onSave={handleOnSubmit}
            onDiscard={handleOnDiscard}
            loading={isLoadingUpdateWidgetSetting || isLoadingWidgetSetting || isSaving || isLoadingUpdateAppearanceText}
          />
          <LegacyTabs tabs={tabs} selected={widgetChoice} onSelect={handleWidgetChoice} />
          <PolarisLayout>
            <PolarisLayout.Section>
              <BlockStack>
                {widgetChoice === WIDGET_SETTING_CHOICE.POSITION && <WidgetPosition shopThemes={shopThemesData} />}
                {widgetChoice === WIDGET_SETTING_CHOICE.DESIGN && <WidgetDesign />}
                <FooterHelpText linkDocs={'https://docs.dingdoong.io/dashboard/dashboard'} isShowContact={true} />
              </BlockStack>
            </PolarisLayout.Section>
            {widgetChoice === WIDGET_SETTING_CHOICE.DESIGN && (
              <StyledSection>
                <PolarisLayout.Section variant="oneThird">
                  <BlockStack gap="300">
                    <WidgetPreview />
                  </BlockStack>
                </PolarisLayout.Section>
              </StyledSection>
            )}
          </PolarisLayout>
        </BlockStack>
      )}
    </Layout>
  );
}
